import { IssueEntity, IssueState } from '@/types';
import {
    CLEAR_PINNED_ISSUE,
    ClearPinnedIssueAction,
    FETCH_ISSUES_SUCCESS,
    FetchIssuesSuccessAction,
    ISSUE_FETCHED,
    ISSUE_UPDATED,
    IssueFetchedAction,
    IssueUpdatedAction,
    PIN_CREATED_ISSUE,
    PinCreatedIssueAction,
} from '@/actions/triage';

const CLOSED_ISSUE_STATES = [IssueState.FixMerged, IssueState.FixRejected, IssueState.NotAPriority];

// Open/closed bucket a state belongs to — drives count adjustments and tab membership below.
const issueStatusGroup = (state: IssueState): 'open' | 'closed' =>
    CLOSED_ISSUE_STATES.includes(state) ? 'closed' : 'open';

export interface IssuesStateForApp {
    issues: IssueEntity[];
    openCount: number;
    closedCount: number;
    // Deep-linked issues fetched on demand (may not belong to the active tab/list)
    coldLoadedById?: Record<string, IssueEntity>;
    pinnedIssue?: IssueEntity;
}

export type IssuesByAppId = Record<string, IssuesStateForApp>;

const emptyForApp: IssuesStateForApp = { issues: [], openCount: 0, closedCount: 0 };

// Hoists the pinned issue to the top of the list, preferring the freshest copy present in the list.
const applyPin = (
    issues: IssueEntity[],
    pinned?: IssueEntity,
): { issues: IssueEntity[]; pinnedIssue?: IssueEntity } => {
    if (!pinned) {
        return { issues, pinnedIssue: undefined };
    }
    const fresh = issues.find((i) => i.id === pinned.id) ?? pinned;
    return { issues: [fresh, ...issues.filter((i) => i.id !== pinned.id)], pinnedIssue: fresh };
};

type IssuesAction =
    | FetchIssuesSuccessAction
    | IssueUpdatedAction
    | IssueFetchedAction
    | PinCreatedIssueAction
    | ClearPinnedIssueAction;

export function issuesReducer(state: IssuesByAppId = {}, action: IssuesAction): IssuesByAppId {
    switch (action.type) {
        case FETCH_ISSUES_SUCCESS: {
            const { appId, issues, offset, openCount, closedCount } = action.payload;
            // Preserve any cold-loaded (deep-linked) issues across list (re)fetches so the route-driven drawer
            // doesn't lose its issue when the list finishes loading.
            const coldLoadedById = state[appId]?.coldLoadedById;
            if (offset === 0) {
                // First page (or filter change): replace, keeping any pinned issue on top.
                const pinned = applyPin(issues, state[appId]?.pinnedIssue);
                return {
                    ...state,
                    [appId]: {
                        issues: pinned.issues,
                        openCount,
                        closedCount,
                        coldLoadedById,
                        pinnedIssue: pinned.pinnedIssue,
                    },
                };
            }
            // Subsequent page: append issues (dedup by Id, latest wins), refresh counts.
            const existing = state[appId] ?? emptyForApp;
            const byId = new Map<string, IssueEntity>();
            for (const issue of existing.issues) byId.set(issue.id, issue);
            for (const issue of issues) byId.set(issue.id, issue);
            const pinned = applyPin(Array.from(byId.values()), existing.pinnedIssue);
            return {
                ...state,
                [appId]: {
                    issues: pinned.issues,
                    openCount,
                    closedCount,
                    coldLoadedById,
                    pinnedIssue: pinned.pinnedIssue,
                },
            };
        }
        case PIN_CREATED_ISSUE: {
            const { appId, issue } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            // Drop any existing copy and prepend so the new issue shows on top immediately (before the refetch).
            const issues = [issue, ...existing.issues.filter((i) => i.id !== issue.id)];
            return { ...state, [appId]: { ...existing, issues, pinnedIssue: issue } };
        }
        // Dispatched by the list on any filter/sort change and on mount; the follow-up fetch then places it naturally.
        case CLEAR_PINNED_ISSUE: {
            const { appId } = action.payload;
            const existing = state[appId];
            if (!existing?.pinnedIssue) {
                return state;
            }
            return { ...state, [appId]: { ...existing, pinnedIssue: undefined } };
        }
        case ISSUE_UPDATED: {
            const { appId, issue, statusGroup } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            const previous = existing.issues.find((i) => i.id === issue.id);

            const leavingTab = !!statusGroup && issueStatusGroup(issue.state) !== statusGroup;
            const deferRemoval = leavingTab && issue.state === IssueState.NotAPriority;

            // Like ISSUE_FETCHED: updates (e.g. saving a description) under-populate `build` (identifier only,
            // no CI/App Store linkage), so keep the richer copy already in the list rather than clobbering it —
            // but only while it still matches the issue's builds. When the build was cleared (e.g. after a
            // version change) `builds` no longer contains it, so drop the stale linkage instead of preserving it.
            const withBuild = (prev?: IssueEntity) => {
                const keepPrev = prev?.build && issue.builds?.includes(prev.build.identifier);
                return { ...issue, build: issue.build ?? (keepPrev ? prev?.build : undefined) };
            };

            // Adjust open/closed totals when the state change crosses buckets (skip while deferring the move).
            let { openCount, closedCount } = existing;
            if (previous && !deferRemoval) {
                const from = issueStatusGroup(previous.state);
                const to = issueStatusGroup(issue.state);
                if (from !== to) {
                    if (from === 'open') openCount = Math.max(0, openCount - 1);
                    else closedCount = Math.max(0, closedCount - 1);
                    if (to === 'open') openCount += 1;
                    else closedCount += 1;
                }
            }

            // Drop the issue when it leaves the active tab; keep it (faded) in place otherwise or while deferring.
            const issues =
                leavingTab && !deferRemoval ?
                    existing.issues.filter((i) => i.id !== issue.id)
                :   existing.issues.map((i) => (i.id === issue.id ? withBuild(i) : i));

            // Keep a cold-loaded copy (if any) fresh too, so the route-driven drawer reflects the update.
            const coldLoadedById =
                existing.coldLoadedById?.[issue.id] ?
                    { ...existing.coldLoadedById, [issue.id]: withBuild(existing.coldLoadedById[issue.id]) }
                :   existing.coldLoadedById;

            // Keep the pin fresh; drop it if the pinned issue just left the active tab.
            let pinnedIssue = existing.pinnedIssue;
            if (pinnedIssue?.id === issue.id) {
                pinnedIssue = leavingTab && !deferRemoval ? undefined : withBuild(pinnedIssue);
            }

            return { ...state, [appId]: { issues, openCount, closedCount, coldLoadedById, pinnedIssue } };
        }
        case ISSUE_FETCHED: {
            const { appId, issue } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            const inList = existing.issues.find((i) => i.id === issue.id);
            // The single-issue fetch under-populates `build` (identifier only, no CI/App Store linkage), so keep the
            // richer copy from the list instead of letting the fetch clobber it — but only while it still matches the
            // issue's builds, so a cleared build doesn't linger.
            const fetched = {
                ...issue,
                build:
                    issue.build ??
                    (inList?.build && issue.builds?.includes(inList.build.identifier) ? inList.build : undefined),
            };
            // Always keep a cold-loaded copy: a list refetch replaces the list with page 0, and without this the
            // drawer loses the issue it's showing whenever that issue isn't on the first page.
            return {
                ...state,
                [appId]: {
                    ...existing,
                    issues: inList ? existing.issues.map((i) => (i.id === issue.id ? fetched : i)) : existing.issues,
                    coldLoadedById: { ...(existing.coldLoadedById ?? {}), [issue.id]: fetched },
                },
            };
        }
        default:
            return state;
    }
}

export default issuesReducer;
